import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import {
    clearHjEnquiryAdminToken,
    getApiBaseUrl,
    getHjEnquiryAdminToken,
    setHjEnquiryAdminToken,
} from '../utils/api';

const todayIsoDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const ENQUIRY_TYPE_OPTIONS = [
    { value: 'all', label: 'All types' },
    { value: 'Loan Enquiry', label: 'Loan' },
    { value: 'Insurance Enquiry', label: 'Insurance' },
    { value: 'Mutual Fund Enquiry', label: 'Mutual Fund' },
    { value: 'Realestate Enquiry', label: 'Real Estate' },
    { value: 'Contact Us Enquiry', label: 'Contact Us' },
];

function AllEnquiryReport() {
    const [token, setToken] = useState(() => getHjEnquiryAdminToken());
    const [otpStep, setOtpStep] = useState('request');
    const [otp, setOtp] = useState('');
    const [otpMessage, setOtpMessage] = useState('');
    const [otpLoading, setOtpLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    const [fromDate, setFromDate] = useState(() => todayIsoDate());
    const [toDate, setToDate] = useState(() => todayIsoDate());
    const [enquiryType, setEnquiryType] = useState('all');
    const [allEnquiryData, setAllEnquiryData] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');

    const baseUrl = getApiBaseUrl();
    const fetchAbortRef = useRef(null);

    useEffect(() => {
        return () => {
            clearHjEnquiryAdminToken();
        };
    }, []);

    const authHeaders = useCallback(() => {
        const t = getHjEnquiryAdminToken();
        return t ? { Authorization: `Bearer ${t}` } : {};
    }, []);

    const handleUnauthorized = useCallback(() => {
        clearHjEnquiryAdminToken();
        setToken('');
        setOtpStep('request');
        setAllEnquiryData([]);
        setLoginError('Session expired. Please log in again.');
    }, []);

    const fetchEnquiries = useCallback(async (overrides = {}) => {
        if (!getHjEnquiryAdminToken()) {
            return;
        }
        if (fetchAbortRef.current) {
            fetchAbortRef.current.abort();
        }
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        const activeFrom = overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
        const activeTo = overrides.toDate !== undefined ? overrides.toDate : toDate;
        const activeType = overrides.enquiryType !== undefined ? overrides.enquiryType : enquiryType;

        setListLoading(true);
        setListError('');
        const params = new URLSearchParams();
        if (activeFrom) params.set('fromDate', activeFrom);
        if (activeTo) params.set('toDate', activeTo);
        if (activeType && activeType !== 'all') params.set('enquiryType', activeType);
        const qs = params.toString();
        const url = `${baseUrl}/hj-enquiry/getall${qs ? `?${qs}` : ''}`;
        try {
            const res = await axios.get(url, {
                headers: authHeaders(),
                signal: controller.signal,
            });
            setAllEnquiryData(res.data?.data || []);
        } catch (err) {
            if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
                return;
            }
            if (err.response?.status === 401 || err.response?.status === 403) {
                handleUnauthorized();
            } else {
                const detail = err.response?.data?.error;
                const base = err.response?.data?.message || 'Failed to load enquiries.';
                setListError(detail ? `${base} (${detail})` : base);
            }
        } finally {
            if (fetchAbortRef.current === controller) {
                setListLoading(false);
                fetchAbortRef.current = null;
            }
        }
    }, [baseUrl, fromDate, toDate, enquiryType, authHeaders, handleUnauthorized]);

    useEffect(() => {
        if (token) {
            fetchEnquiries();
        }
        // Load once after login; use Apply filters to refetch with new dates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const requestOtp = async () => {
        setOtpLoading(true);
        setLoginError('');
        setOtpMessage('');
        try {
            const res = await axios.post(`${baseUrl}/hj-enquiry/admin/request-otp`);
            setOtpMessage(res.data?.message || 'OTP sent.');
            setOtpStep('verify');
        } catch (err) {
            setLoginError(err.response?.data?.message || 'Could not send OTP.');
        } finally {
            setOtpLoading(false);
        }
    };

    const verifyOtp = async (e) => {
        e.preventDefault();
        setOtpLoading(true);
        setLoginError('');
        try {
            const res = await axios.post(`${baseUrl}/hj-enquiry/admin/verify-otp`, { otp });
            const newToken = res.data?.data?.token;
            if (!newToken) {
                setLoginError('Invalid login response.');
                return;
            }
            setHjEnquiryAdminToken(newToken);
            setToken(newToken);
            setOtp('');
            setOtpStep('request');
        } catch (err) {
            setLoginError(err.response?.data?.message || 'Invalid OTP.');
        } finally {
            setOtpLoading(false);
        }
    };

    const getSheetData = (data, header) => {
        const fields = header;
        const sheetData = data.map((row) =>
            fields.map((fieldName) => (row[fieldName] != null ? row[fieldName] : ''))
        );
        sheetData.unshift(header);
        return sheetData;
    };

    const saveAsExcel = () => {
        const header = ['EnquiryDate', 'Name', 'MobileNumber', 'EmailId', 'Remark', 'EnquiryType'];
        const fileDate = new Date().toISOString().slice(0, 10);
        XlsxPopulate.fromBlankAsync().then(async (workbook) => {
            const sheet1 = workbook.sheet(0);
            const sheetData = getSheetData(allEnquiryData, header);
            const totalColumns = sheetData[0].length;
            sheet1.cell('A1').value(sheetData);
            const endColumn = String.fromCharCode(64 + totalColumns);
            sheet1.row(1).style('bold', true);
            sheet1.range(`A1:${endColumn}1`).style('fill', 'BFBFBF');
            sheet1.usedRange().style('border', true);
            return workbook.outputAsync().then((res) => {
                saveAs(res, `hj-enquiries-${fileDate}.xlsx`);
            });
        });
    };

    const printPdf = () => {
        window.print();
    };

    if (!token) {
        return (
            <section className="contact-section pt_120 pb_120">
                <div className="auto-container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 col-md-8">
                            <div className="card p-4 shadow-sm">
                                <h3 className="mb-3">Enquiries report — login</h3>
                                <p className="text-muted small">
                                    An OTP will be sent to the To Email configured in client settings.
                                </p>
                                {loginError && <div className="alert alert-danger">{loginError}</div>}
                                {otpMessage && <div className="alert alert-success">{otpMessage}</div>}
                                {otpStep === 'request' && (
                                    <button
                                        type="button"
                                        className="theme-btn btn-one w-100"
                                        disabled={otpLoading}
                                        onClick={requestOtp}
                                    >
                                        {otpLoading ? 'Sending…' : 'Send OTP'}
                                    </button>
                                )}
                                {otpStep === 'verify' && (
                                    <form onSubmit={verifyOtp}>
                                        <div className="mb-3">
                                            <label className="form-label">Enter 6-digit OTP</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                maxLength={6}
                                                value={otp}
                                                onChange={(ev) => setOtp(ev.target.value.replace(/\D/g, ''))}
                                                autoComplete="one-time-code"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="theme-btn btn-one w-100 mb-2"
                                            disabled={otpLoading || otp.length !== 6}
                                        >
                                            {otpLoading ? 'Verifying…' : 'Verify & open report'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-link w-100"
                                            onClick={() => {
                                                setOtpStep('request');
                                                setOtp('');
                                            }}
                                        >
                                            Resend OTP
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const enquiryData = allEnquiryData.length > 0
        ? allEnquiryData
        : [{ EnquiryDate: '', Name: 'No records found', MobileNumber: '', EmailId: '', Remark: '', EnquiryType: '' }];

    return (
        <Fragment>
            <section className="contact-section pt_120 pb_120" id="hj-enquiry-report">
                <style>
                    {`@media print {
                        header, .hj-enquiry-filters, .hj-enquiry-actions, .no-print { display: none !important; }
                        #hj-enquiry-report { padding-top: 0 !important; }
                    }`}
                </style>
                <div className="auto-container">
                    <h2 className="mb-4">All enquiries</h2>
                    <div className="row g-3 mb-4 hj-enquiry-filters no-print">
                        <div className="col-md-3">
                            <label className="form-label">From date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Enquiry type</label>
                            <select
                                className="form-control"
                                value={enquiryType}
                                onChange={(e) => setEnquiryType(e.target.value)}
                            >
                                {ENQUIRY_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button
                                type="button"
                                className="theme-btn btn-one w-100"
                                onClick={fetchEnquiries}
                                disabled={listLoading}
                            >
                                {listLoading ? 'Loading…' : 'Apply filters'}
                            </button>
                        </div>
                    </div>
                    {listError && <div className="alert alert-danger no-print">{listError}</div>}
                    <div className="hj-enquiry-actions mb-3 no-print">
                        <button type="button" className="theme-btn btn-one me-2" onClick={saveAsExcel} disabled={!allEnquiryData.length}>
                            Export Excel
                        </button>
                        <button type="button" className="theme-btn btn-two" onClick={printPdf} disabled={!allEnquiryData.length}>
                            Print / PDF
                        </button>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th>Date / time</th>
                                    <th>Name</th>
                                    <th>Mobile</th>
                                    <th>Email</th>
                                    <th>Details</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {enquiryData.map((d, i) => (
                                    <tr key={i}>
                                        <td>{d.EnquiryDate}</td>
                                        <td>{d.Name}</td>
                                        <td>{d.MobileNumber}</td>
                                        <td>{d.EmailId}</td>
                                        <td>{d.Remark}</td>
                                        <td>{d.EnquiryType}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </Fragment>
    );
}

export default AllEnquiryReport;
